import DOMPurify from 'dompurify';
import { marked } from 'marked';

import type { LeDataChatMessage, LeDataContentBlock } from '~/types/leData';

import { pickTableFromBlock } from './tableBlock';

export { pickTableFromBlock } from './tableBlock';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

export const PDF_MARGIN_PT = 40;
export const PDF_LINE_HEIGHT_PT = 14;
export const PDF_HEADING_LINE_HEIGHT_PT = 18;
export const PDF_PARAGRAPH_GAP_PT = 6;
export const PDF_BLOCK_GAP_PT = 10;
export const PDF_MESSAGE_GAP_PT = 18;
export const PDF_DEFAULT_FONT_SIZE_PT = 10;
export const PDF_HEADING_FONT_SIZE_PT = 12;
export const PDF_TITLE_FONT_SIZE_PT = 14;
export const PDF_SQL_FONT_SIZE_PT = 9;
export const PDF_CHART_DEFAULT_HEIGHT_PT = 220;
export const PDF_CHART_PADDING_PT = 8;
export const SQL_BG_GREY = 245;
export const ERROR_TEXT_COLOR: [number, number, number] = [220, 50, 47];
export const BLOCK_ID_FILENAME_LENGTH = 8;
export const SESSION_ID_FILENAME_LENGTH = 8;

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface PdfExportOptions {
  filename: string;
  /**
   * For chart capture: a function that resolves a block id to its rendered DOM node so we can html2canvas it.
   * Returns null when the block has no chart node available (e.g. message exported off-screen). In that case
   * the chart is skipped with a "(chart unavailable)" placeholder.
   */
  getChartNode: (blockId: string) => HTMLElement | null;
}

/* -------------------------------------------------------------------------- */
/* Pure helpers (independently testable, no DOM/library mocks needed)          */
/* -------------------------------------------------------------------------- */

function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Replace Unicode characters that jsPDF's standard fonts cannot render.
 * Maps common symbols to ASCII equivalents and strips emoji.
 */
export function sanitizeForPdf(text: string): string {
  return (
    text
      // Arrows
      .replace(/→/g, '->')
      .replace(/←/g, '<-')
      .replace(/↔/g, '<->')
      .replace(/⇒/g, '=>')
      .replace(/⇐/g, '<=')
      .replace(/⇔/g, '<=>')
      .replace(/↑/g, '^')
      .replace(/↓/g, 'v')
      // Math symbols
      .replace(/≈/g, '~')
      .replace(/≠/g, '!=')
      .replace(/≤/g, '<=')
      .replace(/≥/g, '>=')
      .replace(/×/g, 'x')
      .replace(/÷/g, '/')
      .replace(/±/g, '+/-')
      .replace(/∞/g, 'infinity')
      // Bullets and dashes
      .replace(/•/g, '*')
      .replace(/·/g, '*')
      .replace(/—/g, '--')
      .replace(/–/g, '-')
      .replace(/'/g, "'")
      .replace(/'/g, "'")
      .replace(/"/g, '"')
      .replace(/"/g, '"')
      .replace(/…/g, '...')
      // Strip emoji (Unicode emoji ranges)
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
      .replace(/[\u{2600}-\u{26FF}]/gu, '')
      .replace(/[\u{2700}-\u{27BF}]/gu, '')
      .replace(/[\u{FE00}-\u{FE0F}]/gu, '') // Variation selectors
  );
}

/**
 * Check if markdown contains a GFM table (pipe-delimited with header separator).
 */
export function containsMarkdownTable(markdown: string): boolean {
  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const nextLine = lines[i + 1]?.trim() ?? '';
    if (line.includes('|') && /^\|?[\s:-]+\|[\s|:-]*$/.test(nextLine)) {
      return true;
    }
  }
  return false;
}

/**
 * Parse a GFM markdown table into headers and rows.
 * Returns null if the markdown doesn't contain a valid table.
 */
export function parseMarkdownTable(markdown: string): {
  headers: Array<string>;
  rows: Array<Array<string>>;
  beforeTable: string;
  afterTable: string;
} | null {
  const lines = markdown.split('\n');
  let tableStartIndex = -1;
  let separatorIndex = -1;

  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const nextLine = lines[i + 1]?.trim() ?? '';
    if (line.includes('|') && /^\|?[\s:-]+\|[\s|:-]*$/.test(nextLine)) {
      tableStartIndex = i;
      separatorIndex = i + 1;
      break;
    }
  }

  if (tableStartIndex === -1) {
    return null;
  }

  const parseRow = (line: string): Array<string> => {
    return line
      .split('|')
      .map((cell) => cell.trim())
      .filter((_, idx, arr) => {
        const lastIdx = line.endsWith('|') ? arr.length - 1 : arr.length;
        return (idx > 0 && idx < lastIdx) || !line.startsWith('|');
      });
  };

  const headerLine = lines[tableStartIndex].trim();
  const headers = parseRow(headerLine).filter((h) => h.length > 0);

  if (headers.length === 0) {
    return null;
  }

  const rows: Array<Array<string>> = [];
  let tableEndIndex = separatorIndex;

  for (let i = separatorIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line.includes('|')) {
      break;
    }
    const cells = parseRow(line);
    while (cells.length < headers.length) {
      cells.push('');
    }
    rows.push(cells.slice(0, headers.length));
    tableEndIndex = i;
  }

  const beforeTable = lines.slice(0, tableStartIndex).join('\n').trim();
  const afterTable = lines
    .slice(tableEndIndex + 1)
    .join('\n')
    .trim();

  return { headers, rows, beforeTable, afterTable };
}

/**
 * Convert markdown -> HTML -> plain selectable text. Preserves code-block contents (including indentation),
 * and turns inline links into `text (url)` so the URL is preserved when the markdown is flattened.
 * Also sanitizes Unicode characters that jsPDF cannot render.
 */
export function stripMarkdownToPlainText(markdown: string): string {
  if (!markdown) {
    return '';
  }

  /** Inline links first so the URL is preserved past tag stripping. */
  const linkified = markdown.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)');

  const html = marked.parse(linkified, { async: false, breaks: true, gfm: true });
  const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
  const decoded = decodeHtmlEntities(sanitized);

  /** Collapse trailing whitespace per line; collapse 3+ blank lines to 2. Strip leading/trailing blank lines, but preserve in-line indentation. */
  const collapsed = decoded
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .replace(/\n+$/, '');

  return sanitizeForPdf(collapsed);
}

/**
 * Parsed segment of rich text with optional formatting.
 */
interface RichTextSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

/**
 * Parse markdown text into segments with formatting info.
 * Handles **bold**, *italic*, and ***bold italic*** markers.
 */
export function parseRichText(markdown: string): Array<RichTextSegment> {
  const segments: Array<RichTextSegment> = [];
  let remaining = markdown;

  const patterns = [
    { regex: /\*\*\*(.+?)\*\*\*/s, bold: true, italic: true },
    { regex: /\*\*(.+?)\*\*/s, bold: true, italic: false },
    { regex: /\*(.+?)\*/s, bold: false, italic: true },
    { regex: /__(.+?)__/s, bold: true, italic: false },
    { regex: /_(.+?)_/s, bold: false, italic: true },
  ];

  while (remaining.length > 0) {
    let earliestMatch: { index: number; length: number; content: string; bold: boolean; italic: boolean } | null = null;

    for (const pattern of patterns) {
      const match = remaining.match(pattern.regex);
      if (match?.index !== undefined) {
        if (!earliestMatch || match.index < earliestMatch.index) {
          earliestMatch = {
            index: match.index,
            length: match[0].length,
            content: match[1],
            bold: pattern.bold,
            italic: pattern.italic,
          };
        }
      }
    }

    if (!earliestMatch) {
      if (remaining.length > 0) {
        segments.push({ text: remaining, bold: false, italic: false });
      }
      break;
    }

    if (earliestMatch.index > 0) {
      segments.push({ text: remaining.slice(0, earliestMatch.index), bold: false, italic: false });
    }

    segments.push({
      text: earliestMatch.content,
      bold: earliestMatch.bold,
      italic: earliestMatch.italic,
    });

    remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
  }

  return segments;
}

export function pickTextFromBlock(payload: Record<string, unknown>): string {
  const candidates = ['markdown', 'text', 'content', 'message', 'body'] as const;
  for (const key of candidates) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return '';
}

export function pickSqlFromBlock(payload: Record<string, unknown>): { sql: string; dialect: string } {
  const candidates = ['sql', 'content', 'query', 'statement', 'body'] as const;
  let sql = '';
  for (const key of candidates) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      sql = value;
      break;
    }
  }
  const dialect = typeof payload.dialect === 'string' && payload.dialect.trim().length > 0 ? payload.dialect : 'sql';
  return { sql, dialect };
}

export function pickFileFromBlock(payload: Record<string, unknown>): { label: string; url: string } {
  const urlCandidates = ['url', 'href', 'downloadUrl'] as const;
  let url = '';
  for (const key of urlCandidates) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      url = value;
      break;
    }
  }

  const labelCandidates = ['label', 'name', 'filename'] as const;
  let label = '';
  for (const key of labelCandidates) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      label = value;
      break;
    }
  }
  if (!label) {
    label = url;
  }

  return { label, url };
}

export function pickErrorFromBlock(payload: Record<string, unknown>): string {
  const candidates = ['message', 'error', 'detail', 'body'] as const;
  for (const key of candidates) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return 'Unknown error';
}

function buildExportTimestamp(now: Date = new Date()): string {
  return now.toISOString().replace(/[-:.]/g, '');
}

export function buildExportFilename(
  scope: 'conversation' | 'message',
  sessionIdShort: string | null,
  idShort: string,
  now: Date = new Date(),
): string {
  const timestamp = buildExportTimestamp(now);
  const parts = ['askrichard', scope];
  if (sessionIdShort) {
    parts.push(sessionIdShort);
  }
  parts.push(idShort);
  parts.push(timestamp);
  return `${parts.join('-')}.pdf`;
}

/* -------------------------------------------------------------------------- */
/* Browser-only helpers (DOM, jspdf, html2canvas)                              */
/* -------------------------------------------------------------------------- */

/** Defer revokeObjectURL until after the click() has triggered the download.
 *  Firefox can race when revoking synchronously: the download may abort
 *  because the URL is freed before the browser has a chance to read it. */
const REVOKE_OBJECT_URL_DELAY_MS = 1000;

function downloadBlob(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_MS);
}

type JsPdfDocument = {
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } };
  addPage: () => void;
  setFont: (font: string, style?: string) => void;
  setFontSize: (size: number) => void;
  setTextColor: (r: number, g: number, b: number) => void;
  setFillColor: (r: number, g: number, b: number) => void;
  setDrawColor: (r: number, g: number, b: number) => void;
  rect: (x: number, y: number, w: number, h: number, style?: string) => void;
  text: (text: string | Array<string>, x: number, y: number, opts?: Record<string, unknown>) => void;
  splitTextToSize: (text: string, maxWidth: number) => Array<string>;
  line: (x1: number, y1: number, x2: number, y2: number) => void;
  addImage: (
    data: string,
    format: string,
    x: number,
    y: number,
    w: number,
    h: number,
    alias?: string,
    compression?: string,
  ) => void;
  output: (type: 'blob') => Blob;
  lastAutoTable?: { finalY: number };
};

interface DocCursor {
  y: number;
}

function pageBottom(doc: JsPdfDocument): number {
  return doc.internal.pageSize.getHeight() - PDF_MARGIN_PT;
}

function contentWidth(doc: JsPdfDocument): number {
  return doc.internal.pageSize.getWidth() - PDF_MARGIN_PT * 2;
}

function ensureSpace(doc: JsPdfDocument, cursor: DocCursor, needed: number): void {
  if (cursor.y + needed > pageBottom(doc)) {
    doc.addPage();
    cursor.y = PDF_MARGIN_PT;
  }
}

function writeWrappedText(
  doc: JsPdfDocument,
  cursor: DocCursor,
  text: string,
  fontSize: number,
  lineHeight: number,
): void {
  if (!text) {
    return;
  }
  doc.setFontSize(fontSize);
  const sanitized = sanitizeForPdf(text);
  const lines = doc.splitTextToSize(sanitized, contentWidth(doc));
  for (const line of lines) {
    ensureSpace(doc, cursor, lineHeight);
    doc.text(line, PDF_MARGIN_PT, cursor.y);
    cursor.y += lineHeight;
  }
}

/**
 * Write rich text (markdown with bold/italic) to the PDF.
 * Strips markdown formatting and renders as plain text with sanitized Unicode.
 */
function writeRichText(
  doc: JsPdfDocument,
  cursor: DocCursor,
  markdown: string,
  fontSize: number,
  lineHeight: number,
): void {
  if (!markdown) {
    return;
  }

  const plain = stripMarkdownToPlainText(markdown);
  writeWrappedText(doc, cursor, plain, fontSize, lineHeight);
}

function writeHeading(doc: JsPdfDocument, cursor: DocCursor, text: string): void {
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  ensureSpace(doc, cursor, PDF_HEADING_LINE_HEIGHT_PT);
  doc.setFontSize(PDF_HEADING_FONT_SIZE_PT);
  doc.text(text, PDF_MARGIN_PT, cursor.y);
  cursor.y += PDF_HEADING_LINE_HEIGHT_PT;
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(PDF_DEFAULT_FONT_SIZE_PT);
}

function drawDivider(doc: JsPdfDocument, cursor: DocCursor): void {
  ensureSpace(doc, cursor, PDF_MESSAGE_GAP_PT);
  doc.setDrawColor(220, 220, 220);
  doc.line(PDF_MARGIN_PT, cursor.y, doc.internal.pageSize.getWidth() - PDF_MARGIN_PT, cursor.y);
  cursor.y += PDF_MESSAGE_GAP_PT;
}

function renderSqlBlockToPdf(doc: JsPdfDocument, cursor: DocCursor, sqlText: string, dialect: string): void {
  doc.setFont('Helvetica', 'italic');
  doc.setTextColor(120, 120, 120);
  writeWrappedText(doc, cursor, `SQL (${dialect})`, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  doc.setFont('Courier', 'normal');
  doc.setFontSize(PDF_SQL_FONT_SIZE_PT);
  const sanitizedSql = sanitizeForPdf(sqlText);
  const lines = doc.splitTextToSize(sanitizedSql, contentWidth(doc) - PDF_CHART_PADDING_PT * 2);
  const lineHeight = 11;
  const minChunkHeight = lineHeight + PDF_CHART_PADDING_PT * 2;

  /** Render in page-sized chunks. For each chunk we draw a fresh background rect
   *  sized to that chunk's lines, then the lines on top, so the grey box follows
   *  the text across page breaks instead of being clipped at the page boundary. */
  let lineIndex = 0;
  while (lineIndex < lines.length) {
    if (pageBottom(doc) - cursor.y < minChunkHeight) {
      doc.addPage();
      cursor.y = PDF_MARGIN_PT;
    }

    const usableHeight = pageBottom(doc) - cursor.y - PDF_CHART_PADDING_PT * 2;
    const maxLinesThisChunk = Math.max(1, Math.floor(usableHeight / lineHeight));
    const linesThisChunk = Math.min(maxLinesThisChunk, lines.length - lineIndex);
    const chunkHeight = linesThisChunk * lineHeight + PDF_CHART_PADDING_PT * 2;

    doc.setFillColor(SQL_BG_GREY, SQL_BG_GREY, SQL_BG_GREY);
    doc.rect(PDF_MARGIN_PT, cursor.y, contentWidth(doc), chunkHeight, 'F');

    let textY = cursor.y + PDF_CHART_PADDING_PT + lineHeight - 2;
    for (let i = 0; i < linesThisChunk; i += 1) {
      doc.text(lines[lineIndex + i], PDF_MARGIN_PT + PDF_CHART_PADDING_PT, textY);
      textY += lineHeight;
    }

    cursor.y += chunkHeight;
    lineIndex += linesThisChunk;

    if (lineIndex < lines.length) {
      doc.addPage();
      cursor.y = PDF_MARGIN_PT;
    }
  }

  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(PDF_DEFAULT_FONT_SIZE_PT);
}

function stringifyTableCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  let str: string;
  if (typeof value === 'object') {
    try {
      str = JSON.stringify(value);
    } catch {
      str = String(value);
    }
  } else {
    str = String(value);
  }
  return sanitizeForPdf(str);
}

async function renderTableBlockToPdf(
  doc: JsPdfDocument,
  cursor: DocCursor,
  block: LeDataContentBlock,
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void,
): Promise<void> {
  const { headers, rows } = pickTableFromBlock(block.payload);
  if (headers.length === 0 && rows.length === 0) {
    writeWrappedText(doc, cursor, '(No table rows)', PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
    return;
  }

  const tableHead = [headers];
  const tableBody = rows.map((row) => row.map((cell) => stringifyTableCell(cell)));

  autoTable(doc, {
    head: tableHead,
    body: tableBody,
    startY: cursor.y,
    margin: { left: PDF_MARGIN_PT, right: PDF_MARGIN_PT },
    styles: { fontSize: PDF_SQL_FONT_SIZE_PT, cellPadding: 4 },
    headStyles: { fillColor: [60, 60, 60], textColor: 255 },
    theme: 'grid',
  });

  const finalY = doc.lastAutoTable?.finalY;
  if (typeof finalY !== 'number') {
    /**
     * autoTable should always populate lastAutoTable. If it does not (older plugin
     * version, mocked doc, etc.) we force a page break rather than silently overlap
     * the rendered rows with subsequent content.
     */
    doc.addPage();
    cursor.y = PDF_MARGIN_PT;
    return;
  }
  cursor.y = finalY + PDF_BLOCK_GAP_PT * 2;
}

async function renderChartByIdToPdf(
  doc: JsPdfDocument,
  cursor: DocCursor,
  chartId: string,
  options: PdfExportOptions,
  html2canvasFn: (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>,
): Promise<void> {
  const node = options.getChartNode(chartId);
  if (!node) {
    doc.setFont('Helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    writeWrappedText(doc, cursor, '[chart unavailable in export]', PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    return;
  }

  try {
    const canvas = await html2canvasFn(node, { backgroundColor: '#ffffff', scale: 2, logging: false });
    const dataUrl = canvas.toDataURL('image/png');
    const width = contentWidth(doc);
    const aspectRatio = canvas.width === 0 ? 1 : canvas.height / canvas.width;
    const height = Math.min(width * aspectRatio, PDF_CHART_DEFAULT_HEIGHT_PT * 2);

    ensureSpace(doc, cursor, height + PDF_BLOCK_GAP_PT);
    doc.addImage(dataUrl, 'PNG', PDF_MARGIN_PT, cursor.y, width, height);
    cursor.y += height + PDF_BLOCK_GAP_PT;
  } catch {
    doc.setFont('Helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    writeWrappedText(doc, cursor, '[chart unavailable in export]', PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }
}

function renderFileBlockToPdf(doc: JsPdfDocument, cursor: DocCursor, block: LeDataContentBlock): void {
  const { label, url } = pickFileFromBlock(block.payload);
  if (!url) {
    writeWrappedText(doc, cursor, '(Missing file URL)', PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
    return;
  }
  writeWrappedText(doc, cursor, `File: ${label} (${url})`, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
}

function renderErrorBlockToPdf(doc: JsPdfDocument, cursor: DocCursor, block: LeDataContentBlock): void {
  const message = pickErrorFromBlock(block.payload);
  doc.setTextColor(...ERROR_TEXT_COLOR);
  writeWrappedText(doc, cursor, `Error: ${message}`, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
  doc.setTextColor(0, 0, 0);
}

async function renderMarkdownTableToPdf(
  doc: JsPdfDocument,
  cursor: DocCursor,
  headers: Array<string>,
  rows: Array<Array<string>>,
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void,
): Promise<void> {
  if (headers.length === 0 && rows.length === 0) {
    return;
  }

  const sanitizedHeaders = headers.map((h) => sanitizeForPdf(h));
  const sanitizedRows = rows.map((row) => row.map((cell) => sanitizeForPdf(cell)));

  autoTable(doc, {
    head: [sanitizedHeaders],
    body: sanitizedRows,
    startY: cursor.y,
    margin: { left: PDF_MARGIN_PT, right: PDF_MARGIN_PT },
    styles: { fontSize: PDF_SQL_FONT_SIZE_PT, cellPadding: 4 },
    headStyles: { fillColor: [60, 60, 60], textColor: 255 },
    theme: 'grid',
  });

  const finalY = doc.lastAutoTable?.finalY;
  if (typeof finalY === 'number') {
    cursor.y = finalY + PDF_BLOCK_GAP_PT * 2;
  } else {
    doc.addPage();
    cursor.y = PDF_MARGIN_PT;
  }
}

/** Fenced chart pattern — mirrors ContentBlockView's findNextEmbeddedChartPayload
 *  and svc-reporting agent.ts CHART_BLOCK_PATTERN. Source of truth lives in
 *  ContentBlockView; keep this regex aligned. */
const CHART_FENCE_PATTERN = /```chart\s*\n([\s\S]*?)```/g;

async function renderTextBlockToPdf(
  doc: JsPdfDocument,
  cursor: DocCursor,
  blockId: string,
  markdown: string,
  options: PdfExportOptions,
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void,
  html2canvasFn: (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>,
): Promise<void> {
  if (!markdown) {
    return;
  }

  // Charts arrive fenced inside the agent's text output. Split them out so they
  // render as captured images instead of falling through as raw JSON. Chart ids
  // here must match the data-le-data-chart-id stamped by ContentBlockView, which
  // uses the segment-array index — so we count both markdown and chart segments.
  // Using matchAll avoids the mutable lastIndex footgun of shared global regexes.
  const charts: Array<{ start: number; end: number }> = [];
  for (const m of markdown.matchAll(CHART_FENCE_PATTERN)) {
    try {
      const parsed = JSON.parse(m[1].trim()) as unknown;
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as Record<string, unknown>).data)) {
        const start = m.index ?? 0;
        charts.push({ start, end: start + m[0].length });
      }
    } catch {
      // malformed JSON inside fence — fall through and let it render as text
    }
  }

  if (charts.length > 0) {
    let cursorPos = 0;
    let segmentIndex = 0;
    for (const chart of charts) {
      const before = markdown.slice(cursorPos, chart.start).trim();
      if (before) {
        await renderTextBlockToPdf(doc, cursor, blockId, before, options, autoTable, html2canvasFn);
        segmentIndex += 1;
      }
      await renderChartByIdToPdf(doc, cursor, `${blockId}-chart-${segmentIndex}`, options, html2canvasFn);
      segmentIndex += 1;
      cursorPos = chart.end;
    }
    const after = markdown.slice(cursorPos).trim();
    if (after) {
      await renderTextBlockToPdf(doc, cursor, blockId, after, options, autoTable, html2canvasFn);
    }
    return;
  }

  if (containsMarkdownTable(markdown)) {
    const parsed = parseMarkdownTable(markdown);
    if (parsed) {
      if (parsed.beforeTable) {
        writeRichText(doc, cursor, parsed.beforeTable, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
      }
      await renderMarkdownTableToPdf(doc, cursor, parsed.headers, parsed.rows, autoTable);
      if (parsed.afterTable) {
        await renderTextBlockToPdf(doc, cursor, blockId, parsed.afterTable, options, autoTable, html2canvasFn);
      }
      return;
    }
  }

  writeRichText(doc, cursor, markdown, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
}

async function renderBlockToPdf(
  doc: JsPdfDocument,
  cursor: DocCursor,
  block: LeDataContentBlock,
  options: PdfExportOptions,
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void,
  html2canvasFn: (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>,
): Promise<void> {
  switch (block.type) {
    case 'text': {
      const raw = pickTextFromBlock(block.payload);
      await renderTextBlockToPdf(doc, cursor, block.id, raw, options, autoTable, html2canvasFn);
      cursor.y += PDF_PARAGRAPH_GAP_PT;
      break;
    }
    case 'sql': {
      const { sql, dialect } = pickSqlFromBlock(block.payload);
      if (sql) {
        renderSqlBlockToPdf(doc, cursor, sql, dialect);
      }
      cursor.y += PDF_BLOCK_GAP_PT;
      break;
    }
    case 'table': {
      await renderTableBlockToPdf(doc, cursor, block, autoTable);
      break;
    }
    case 'chart': {
      await renderChartByIdToPdf(doc, cursor, block.id, options, html2canvasFn);
      break;
    }
    case 'file': {
      renderFileBlockToPdf(doc, cursor, block);
      cursor.y += PDF_PARAGRAPH_GAP_PT;
      break;
    }
    case 'error': {
      renderErrorBlockToPdf(doc, cursor, block);
      cursor.y += PDF_PARAGRAPH_GAP_PT;
      break;
    }
    default: {
      const raw = pickTextFromBlock(block.payload);
      await renderTextBlockToPdf(doc, cursor, block.id, raw, options, autoTable, html2canvasFn);
      cursor.y += PDF_PARAGRAPH_GAP_PT;
    }
  }
}

function writeCoverPage(doc: JsPdfDocument, cursor: DocCursor): void {
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(PDF_TITLE_FONT_SIZE_PT);
  ensureSpace(doc, cursor, PDF_HEADING_LINE_HEIGHT_PT);
  doc.text('askRichard conversation export', PDF_MARGIN_PT, cursor.y);
  cursor.y += PDF_HEADING_LINE_HEIGHT_PT;
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(PDF_DEFAULT_FONT_SIZE_PT);
  doc.setTextColor(120, 120, 120);
  writeWrappedText(doc, cursor, `Exported: ${new Date().toISOString()}`, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
  doc.setTextColor(0, 0, 0);
  cursor.y += PDF_BLOCK_GAP_PT;
}

async function renderMessage(
  doc: JsPdfDocument,
  cursor: DocCursor,
  message: LeDataChatMessage,
  options: PdfExportOptions,
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void,
  html2canvasFn: (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>,
): Promise<void> {
  const idShort = message.id.slice(0, BLOCK_ID_FILENAME_LENGTH);
  const roleLabel = message.role === 'user' ? 'User' : 'Richard';
  writeHeading(doc, cursor, `${roleLabel}  -  ${idShort}`);

  if (message.text) {
    writeRichText(doc, cursor, message.text, PDF_DEFAULT_FONT_SIZE_PT, PDF_LINE_HEIGHT_PT);
  }

  for (const block of message.blocks ?? []) {
    await renderBlockToPdf(doc, cursor, block, options, autoTable, html2canvasFn);
  }
}

function writeSqlAppendix(doc: JsPdfDocument, cursor: DocCursor, messages: ReadonlyArray<LeDataChatMessage>): void {
  const sqlEntries: Array<{ messageIdShort: string; sql: string; dialect: string }> = [];
  for (const message of messages) {
    for (const block of message.blocks ?? []) {
      if (block.type === 'sql') {
        const { sql, dialect } = pickSqlFromBlock(block.payload);
        if (sql) {
          sqlEntries.push({
            messageIdShort: message.id.slice(0, BLOCK_ID_FILENAME_LENGTH),
            sql,
            dialect,
          });
        }
      }
    }
  }
  if (sqlEntries.length === 0) {
    return;
  }

  doc.addPage();
  cursor.y = PDF_MARGIN_PT;
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(PDF_TITLE_FONT_SIZE_PT);
  doc.text('SQL Appendix', PDF_MARGIN_PT, cursor.y);
  cursor.y += PDF_HEADING_LINE_HEIGHT_PT + PDF_BLOCK_GAP_PT;
  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(PDF_DEFAULT_FONT_SIZE_PT);

  for (const entry of sqlEntries) {
    writeHeading(doc, cursor, `From message ${entry.messageIdShort}`);
    renderSqlBlockToPdf(doc, cursor, entry.sql, entry.dialect);
    cursor.y += PDF_BLOCK_GAP_PT;
  }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

async function buildDoc(): Promise<{
  doc: JsPdfDocument;
  autoTable: (doc: JsPdfDocument, options: Record<string, unknown>) => void;
  html2canvasFn: (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>;
}> {
  const [{ jsPDF }, autoTableModule, html2canvasModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    import('html2canvas'),
  ]);
  const autoTable = (autoTableModule.default ?? autoTableModule) as (
    doc: JsPdfDocument,
    options: Record<string, unknown>,
  ) => void;
  const html2canvasFn = (html2canvasModule.default ?? html2canvasModule) as (
    el: HTMLElement,
    opts?: Record<string, unknown>,
  ) => Promise<{ toDataURL: (type: string) => string; width: number; height: number }>;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' }) as unknown as JsPdfDocument;
  return { doc, autoTable, html2canvasFn };
}

export async function exportConversationToPdf(
  messages: ReadonlyArray<LeDataChatMessage>,
  options: PdfExportOptions,
): Promise<void> {
  const { doc, autoTable, html2canvasFn } = await buildDoc();
  const cursor: DocCursor = { y: PDF_MARGIN_PT };

  writeCoverPage(doc, cursor);

  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index];
    await renderMessage(doc, cursor, message, options, autoTable, html2canvasFn);
    if (index < messages.length - 1) {
      drawDivider(doc, cursor);
    }
  }

  writeSqlAppendix(doc, cursor, messages);

  const blob = doc.output('blob');
  downloadBlob(blob, options.filename);
}

export async function exportMessageToPdf(message: LeDataChatMessage, options: PdfExportOptions): Promise<void> {
  const { doc, autoTable, html2canvasFn } = await buildDoc();
  const cursor: DocCursor = { y: PDF_MARGIN_PT };

  writeCoverPage(doc, cursor);
  await renderMessage(doc, cursor, message, options, autoTable, html2canvasFn);
  writeSqlAppendix(doc, cursor, [message]);

  const blob = doc.output('blob');
  downloadBlob(blob, options.filename);
}
